import { useState, useCallback, useMemo } from "react";
import { useAtom } from "jotai";
import {
  plantAtom,
  plantUIAtom,
  fetchProjectPlantsAtom,
  fetchOrgPlantsAtom,
  createPlantAtom,
  updatePlantAtom,
  deletePlantAtom,
  deallocatePlantAtom,
  importPlantsAtom,
  currentPlantsAtom,
} from "../atoms/staff_and_plant/plantAtom";

export const usePlantManagement = () => {
  const [plantState] = useAtom(plantAtom);
  const [uiState, setUIState] = useAtom(plantUIAtom);
  const [, fetchProjectPlants] = useAtom(fetchProjectPlantsAtom);
  const [, fetchOrgPlants] = useAtom(fetchOrgPlantsAtom);
  const [, createPlantAction] = useAtom(createPlantAtom);
  const [, updatePlantAction] = useAtom(updatePlantAtom);
  const [, deletePlantAction] = useAtom(deletePlantAtom);
  const [, deallocatePlantAction] = useAtom(deallocatePlantAtom);
  const [, importPlantsAction] = useAtom(importPlantsAtom);
  const [currentPlants] = useAtom(currentPlantsAtom);

  // Computed values
  const isLoading = plantState.loading;
  const error = plantState.error;
  const activeTab = uiState.activeTab;
  const searchQuery = uiState.searchQuery;
  const selectedPlants = uiState.selectedPlants;
  const orgPlants = plantState.orgPlants;

  // Actions
  const setActiveTab = useCallback(
    (tab) => {
      setUIState((prev) => ({ ...prev, activeTab: tab }));
    },
    [setUIState]
  );

  const setSearchQuery = useCallback(
    (query) => {
      setUIState((prev) => ({ ...prev, searchQuery: query }));
    },
    [setUIState]
  );

  const togglePlantSelection = useCallback(
    (plantId) => {
      setUIState((prev) => ({
        ...prev,
        selectedPlants: prev.selectedPlants.includes(plantId)
          ? prev.selectedPlants.filter((id) => id !== plantId)
          : [...prev.selectedPlants, plantId],
      }));
    },
    [setUIState]
  );

  const clearSelection = useCallback(() => {
    setUIState((prev) => ({ ...prev, selectedPlants: [] }));
  }, [setUIState]);

  const loadPlants = useCallback(async () => {
    try {
      // Load both project and org plants
      await Promise.all([fetchProjectPlants(), fetchOrgPlants()]);
    } catch (error) {
      console.error("Error loading plants:", error);
      throw error;
    }
  }, [fetchProjectPlants, fetchOrgPlants]);

  const createPlant = useCallback(
    async (plantData) => {
      try {
        return await createPlantAction(plantData);
      } catch (error) {
        console.error("Error creating plant:", error);
        throw error;
      }
    },
    [createPlantAction]
  );

  const updatePlant = useCallback(
    async ({ plantId, plantData }) => {
      try {
        return await updatePlantAction({ plantId, plantData });
      } catch (error) {
        console.error("Error updating plant:", error);
        throw error;
      }
    },
    [updatePlantAction]
  );

  const deletePlant = useCallback(
    async (plantId) => {
      try {
        return await deletePlantAction(plantId);
      } catch (error) {
        console.error("Error deleting plant:", error);
        throw error;
      }
    },
    [deletePlantAction]
  );

  const deallocatePlant = useCallback(
    async (plantId) => {
      try {
        return await deallocatePlantAction(plantId);
      } catch (error) {
        console.error("Error deallocating plant:", error);
        throw error;
      }
    },
    [deallocatePlantAction]
  );

  const importPlants = useCallback(
    async (plantIds) => {
      try {
        return await importPlantsAction(plantIds);
      } catch (error) {
        console.error("Error importing plants:", error);
        throw error;
      }
    },
    [importPlantsAction]
  );

  return {
    currentPlants,
    orgPlants,
    isLoading,
    error,
    activeTab,
    searchQuery,
    selectedPlants,
    setActiveTab,
    setSearchQuery,
    togglePlantSelection,
    clearSelection,
    loadPlants,
    createPlant,
    updatePlant,
    deletePlant,
    deallocatePlant,
    importPlants,
  };
};

export const usePlantForm = () => {
  const [formData, setFormData] = useState({
    nickname: '',
    plant_id: '',
    plant_type: '',
    make: '',
    model: '',
    registration_number: '',
    date_on_site: null,
    document: [],
    profile_image: null,
  });

  const [formErrors, setFormErrors] = useState({});

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [formErrors]);

  const addDocument = useCallback((document) => {
    setFormData(prev => ({
      ...prev,
      document: [...prev.document, document]
    }));
  }, []);

  const removeDocument = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      document: prev.document.filter((_, i) => i !== index)
    }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.nickname?.trim()) {
      errors.nickname = 'Nickname is required';
    }
    
    if (!formData.plant_id?.trim()) {
      errors.plant_id = 'Plant ID is required';
    }
    
    if (!formData.registration_number?.trim()) {
      errors.registration_number = 'Registration Number is required';
    }

    if (!formData.date_on_site) {
      errors.date_on_site = 'Date on site is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({
      nickname: '',
      plant_id: '',
      plant_type: '',
      make: '',
      model: '',
      registration_number: '',
      date_on_site: null,
      document: [],
      profile_image: null,
    });
    setFormErrors({});
  }, []);

  const setFormDataFromPlant = useCallback((plant) => {
    setFormData({
      id: plant.id,
      nickname: plant.nickname || plant.name || '',
      plant_id: plant.plant_id || '',
      plant_type: plant.plant_type || '',
      make: plant.make || '',
      model: plant.model || '',
      registration_number: plant.registration_number || '',
      date_on_site: plant.date || plant.date_on_site
        ? new Date(
            typeof (plant.date || plant.date_on_site) === 'number'
              ? (plant.date || plant.date_on_site) * 1000
              : (plant.date || plant.date_on_site)
          )
        : null,
      document: plant.document || plant.documents || [],
      profile_image: plant.profile_image || null,
    });
    setFormErrors({});
  }, []);

  const isValid = useMemo(() => {
    return formData.nickname?.trim() && 
           formData.plant_id?.trim() && 
           formData.registration_number?.trim() && 
           formData.date_on_site;
  }, [formData]);

  return {
    formData,
    formErrors,
    updateField,
    addDocument,
    removeDocument,
    validateForm,
    resetForm,
    setFormDataFromPlant,
    isValid,
  };
};